import { faEraser, faPen, faSlash, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";

export type DrawingMode = "none" | "pen" | "eraser";

const COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#a855f7", "#ffffff"];

type Props = {
    onModeChanged: (mode: DrawingMode) => void;
    onColorChanged: (color: string) => void;
    onExit?: () => void;
};

export default function DrawingControls({ onModeChanged, onColorChanged, onExit }: Props) {
    const [mode, setMode] = useState<DrawingMode>("pen");
    const [color, setColor] = useState(COLORS[0]);

    function changeMode(newMode: DrawingMode) {
        if (newMode === "none") {
            // Unpin / exit annotation — parent handles focus restore
            onExit?.();
            setMode("pen");
            return;
        }
        setMode(newMode);
        onModeChanged(newMode);
    }

    function changeColor(newColor: string) {
        setColor(newColor);
        onColorChanged(newColor);
        // Selecting a color switches back to pen so drawing is ready immediately
        if (mode !== "pen") {
            setMode("pen");
            onModeChanged("pen");
        }
    }

    return (
        <div className="drawing-toolbar">
            <div className="drawing-toolbar-inner">
                <button
                    className={`draw-btn ${mode === 'pen' ? 'active' : ''}`}
                    onClick={() => changeMode("pen")}
                    title="Pen"
                >
                    <FontAwesomeIcon icon={faPen} />
                </button>

                <button
                    className={`draw-btn ${mode === 'eraser' ? 'active' : ''}`}
                    onClick={() => changeMode("eraser")}
                    title="Eraser"
                >
                    <FontAwesomeIcon icon={faEraser} />
                </button>

                <div className="draw-divider" />

                {COLORS.map(c => (
                    <button
                        key={c}
                        className={`color-swatch ${color === c ? 'selected' : ''}`}
                        style={{ background: c }}
                        onClick={() => changeColor(c)}
                        title={c}
                    />
                ))}

                <div className="draw-divider" />

                <button
                    type="button"
                    className="draw-btn clear-btn"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        changeMode("none");
                    }}
                    title="Exit annotation"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            <style>{`
                .drawing-toolbar {
                    display: flex;
                    justify-content: center;
                }

                .drawing-toolbar-inner {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    background: rgba(15, 17, 23, 0.85);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    border-radius: 40px;
                    padding: 0.5rem 0.75rem;
                    backdrop-filter: blur(12px);
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
                }

                .draw-btn {
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.85rem;
                    transition: all 0.15s;
                }

                .draw-btn:hover {
                    background: rgba(255, 255, 255, 0.1);
                    color: #e2e8f0;
                }

                .draw-btn.active {
                    background: var(--hx-blue);
                    color: white;
                }

                .draw-btn.clear-btn:hover {
                    background: rgba(239, 68, 68, 0.2);
                    color: #f87171;
                }

                .draw-divider {
                    width: 1px;
                    height: 20px;
                    background: rgba(255, 255, 255, 0.1);
                    margin: 0 0.125rem;
                }

                .color-swatch {
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    border: 2px solid transparent;
                    cursor: pointer;
                    transition: all 0.15s;
                    flex-shrink: 0;
                    outline: none;
                }

                .color-swatch:hover {
                    transform: scale(1.2);
                }

                .color-swatch.selected {
                    border-color: white;
                    transform: scale(1.15);
                    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
                }

                @media (max-width: 768px) {
                    .drawing-toolbar-inner {
                        gap: 0.25rem;
                        padding: 0.4rem 0.55rem;
                        max-width: calc(100vw - 2rem);
                    }
                    .draw-btn {
                        width: 32px;
                        height: 32px;
                        flex-shrink: 0;
                    }
                    .color-swatch {
                        width: 18px;
                        height: 18px;
                        flex-shrink: 0;
                    }
                }
            `}</style>
        </div>
    );
}
